import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Lock, Loader2, Zap } from "lucide-react";

const FEATURES = [
  "Chain-of-custody tracking",
  "DEA audit-ready reports",
  "Unlimited staff accounts",
  "Unlimited transaction logs",
  "Automated compliance reminders",
  "Role-based access control",
  "Custom report templates",
  "Priority support + onboarding",
  "Data encryption & automatic backups",
  "99.9% uptime SLA",
];

interface PlanPrice {
  amount: number;
  currency: string;
}

interface SubscriptionPlansProps {
  pharmacyName: string;
  prices: { monthly: PlanPrice; yearly: PlanPrice };
  onSelectPlan: (plan: "monthly" | "yearly") => Promise<void>;
}

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: "easeOut" },
  },
};

function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount / 100);
}

export function SubscriptionPlans({
  pharmacyName,
  prices,
  onSelectPlan,
}: SubscriptionPlansProps) {
  const [annual, setAnnual] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const plan = annual ? "yearly" : "monthly";
  const currency = prices.monthly.currency;

  // Stripe returns yearly.amount as the full-year unit_amount (e.g. 150000 = $1,500)
  const monthlyPrice = prices.monthly.amount;
  const yearlyTotal = prices.yearly.amount;
  const yearlyMonthly = Math.round(yearlyTotal / 12);
  const displayAmount = annual ? yearlyMonthly : monthlyPrice;
  const annualSavings = monthlyPrice * 12 - yearlyTotal;

  const handleSelect = async () => {
    setIsLoading(true);
    try {
      await onSelectPlan(plan);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.1 } },
      }}
      className="w-full max-w-2xl mx-auto"
    >
      {/* Header */}
      <motion.div variants={fadeUp} className="text-center mb-8">
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary mb-4">
          <Zap className="h-3.5 w-3.5" />
          Almost there
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
          Welcome, {pharmacyName}
        </h1>
        <p className="mt-2 text-base text-muted-foreground max-w-lg mx-auto leading-relaxed">
          One plan. Everything included. Choose your billing cycle to get
          started.
        </p>
      </motion.div>

      {/* Billing toggle */}
      <motion.div
        variants={fadeUp}
        className="flex items-center justify-center gap-3 mb-8"
      >
        <span
          className={`text-sm font-medium transition-colors ${
            annual ? "text-muted-foreground" : "text-foreground"
          }`}
        >
          Monthly
        </span>
        <button
          onClick={() => setAnnual((v) => !v)}
          className="relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none"
          style={{ background: annual ? "#1F5FA8" : "#CBD5E1" }}
          aria-label="Toggle billing period"
        >
          <span
            className="absolute left-0 top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200"
            style={{
              transform: annual ? "translateX(26px)" : "translateX(2px)",
            }}
          />
        </button>
        <span
          className={`flex items-center gap-2 text-sm font-medium transition-colors ${
            annual ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          Annual
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-secondary/10 text-secondary border border-secondary/25">
            Get 2 months free
          </span>
        </span>
      </motion.div>

      {/* Plan card */}
      <motion.div
        variants={fadeUp}
        className="rounded-2xl overflow-hidden border border-primary"
        style={{
          boxShadow:
            "0 8px 48px rgba(31, 95, 168, 0.12), 0 0 0 1px rgba(31, 95, 168, 0.1)",
        }}
      >
        {/* Top bar */}
        <div
          className="px-8 py-3 text-center text-xs font-bold text-white tracking-widest uppercase"
          style={{ background: "linear-gradient(135deg, #1F5FA8, #2B7ACE)" }}
        >
          Everything Included
        </div>

        <div className="bg-surface p-8 sm:p-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8">
            {/* Pricing */}
            <div>
              <div className="flex items-end gap-1 mb-1">
                <motion.span
                  key={displayAmount}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="text-6xl font-extrabold text-foreground leading-none"
                >
                  {formatPrice(displayAmount, currency)}
                </motion.span>
                <span className="text-base text-text-secondary mb-1">
                  /month
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {annual
                  ? `Billed annually · ${formatPrice(yearlyTotal, currency)}/year`
                  : `Billed monthly · Switch to annual and save ${formatPrice(annualSavings, currency)}/year`}
              </p>
            </div>

            {/* CTA */}
            <div className="flex flex-col gap-2">
              <button
                onClick={handleSelect}
                disabled={isLoading}
                className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-white text-sm transition-all duration-200 hover:scale-[1.02] shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: "linear-gradient(135deg, #1F5FA8, #2B7ACE)",
                  boxShadow: "0 4px 20px rgba(31, 95, 168, 0.35)",
                }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Redirecting to checkout...
                  </>
                ) : (
                  <>
                    Get Started
                    <ArrowRight
                      size={15}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </>
                )}
              </button>
              <div className="flex items-center gap-1.5 justify-center">
                <Lock size={11} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  No contracts · Cancel anytime
                </span>
              </div>
            </div>
          </div>

          {/* Features grid */}
          <div className="border-t border-border pt-8">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-5">
              What&apos;s included
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {FEATURES.map((feature) => (
                <li key={feature} className="flex items-start gap-2.5">
                  <CheckCircle2
                    size={16}
                    className="shrink-0 mt-0.5 text-primary"
                  />
                  <span className="text-sm text-text-secondary">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
